import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { ROUTES, RouteName } from '../../Constants/routes';
import { buttonClicked } from '../../api/mParticlesApi/mParticleEvents/generic/genericEvents';
import AnimationLogo from '../../components/AnimationLogo/AnimationLogo';
import * as AuthFormUi from '../../components/AuthForm/AuthForm.styles';
import { BackgroundChanging } from '../../components/BackgroundChanging/BackgroundChanging';
import { Col } from '../../components/Common';
import { useEnv } from '../../components/EnvProvider/EnvProvider';
import { Layout } from '../../components/Layout/Layout';
import { LEGAL_LINK } from '../../components/LegalLink/LegalLink';
import { Loader } from '../../components/Loader/Loader';
import { PromotionModal } from '../../components/Modals';
import SponsorLogo from '../../components/SponsorLogo/SponsorLogo';
import { usePromotionUnauthorized } from '../../hooks/promotions/usePromotionUnauthorized';
import { useExtendedNavigate } from '../../hooks/useExtendedNavigate';
import { useForm } from '../../hooks/useForm';
import { useTranslations } from '../../hooks/useTranslations';
import { FirstLoadProps } from '../../routes/Routes';
import { isAuthedSelector } from '../../store/auth/auth.selectors';
import { AppDispatch } from '../../store/store';
import { searchAccountData, userDataSelector } from '../../store/userData';
import { zoneInfoStateSelector } from '../../store/zoneInfo';
import { CheckEmailFormFields, checkEmailSchema } from '../../utils/validations';
import * as Ui from './Welcome.styles';

const Welcome = ({ setFirstLoad, firstLoad }: FirstLoadProps) => {
  const { PARTNER_LOGO_VISIBILITY } = useEnv();
  const entryPageRef = useRef<string>();
  const skipFavoriteTeamsRef = useRef(false);
  const location = useLocation();
  const t = useTranslations();
  const navigate = useExtendedNavigate();
  const { loading, userNameForCreateAccount, email } = useSelector(userDataSelector);
  const { isAllowedCountry } = useSelector(zoneInfoStateSelector);
  const isAuthed = useSelector(isAuthedSelector);

  const dispatch: AppDispatch = useDispatch();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    criteriaMode: 'all',
    defaultValues: {
      [CheckEmailFormFields.Email]: userNameForCreateAccount || email.toLowerCase() || '',
    },
    mode: 'onTouched',
    resolver: yupResolver(checkEmailSchema),
  });

  useEffect(() => {
    if (firstLoad) {
      entryPageRef.current = location.state?.redirectTo;
    }
  }, [firstLoad, location]);

  const { customSteps, promotion, promotionUrl, showPromotionModal, skipFavoriteTeams } =
    usePromotionUnauthorized(!firstLoad);

  useEffect(() => {
    skipFavoriteTeamsRef.current = !!skipFavoriteTeams;
  }, [skipFavoriteTeams]);

  useEffect(() => {
    if (promotionUrl) {
      entryPageRef.current = promotionUrl;
    }
  }, [promotionUrl]);

  const handleOnSuccess = useCallback(() => {
    navigate(ROUTES.login, {
      state: { redirectTo: entryPageRef.current },
    });

    buttonClicked({
      destination: ROUTES[RouteName.Login],
      location: 'Module: welcome',
      path: window.location.pathname,
      screen: 'Welcome',
      text: t.welcome_submitButton,
      type: 'Submit',
      url: window.location.href,
    });
  }, [navigate, entryPageRef.current]);

  const handleOnFailure = useCallback(
    (isServerError?: boolean) => {
      if (isServerError === true) {
        navigate(ROUTES[RouteName.ServerError], { replace: true });
        return;
      }

      const route = isAllowedCountry ? ROUTES[RouteName.Signup] : ROUTES[RouteName.ForbiddenRegion];

      navigate(route, {
        replace: true,
        state: {
          redirectTo: entryPageRef.current,
          skipFavoriteTeams: skipFavoriteTeamsRef.current,
        },
      });

      buttonClicked({
        destination: route,
        location: 'Module: welcome',
        path: window.location.pathname,
        screen: 'Welcome',
        text: t.welcome_submitButton,
        type: 'Submit',
        url: window.location.href,
      });
    },
    [isAllowedCountry, navigate, entryPageRef.current],
  );

  const onSubmit = useCallback(
    ({ email }: { [CheckEmailFormFields.Email]: string }) => {
      dispatch(
        searchAccountData({
          email,
          failureCallback: handleOnFailure,
          successCallback: handleOnSuccess,
        }),
      );
    },
    [dispatch, handleOnFailure, handleOnSuccess],
  );

  const fieldErrors = errors[CheckEmailFormFields.Email];

  if (firstLoad) {
    return (
      <Layout noNavbar noFooterOneTrust>
        <Ui.FirstLoadContentContainer>
          <AnimationLogo setFirstLoad={setFirstLoad} />
          {PARTNER_LOGO_VISIBILITY && <SponsorLogo />}
        </Ui.FirstLoadContentContainer>
      </Layout>
    );
  }

  if (isAuthed) {
    navigate(ROUTES[RouteName.Home], { replace: true });
  }

  return (
    <Layout noNavbar noZipBar>
      {loading && <Loader absolute withBackdrop />}

      <Ui.ContentWrapper>
        <BackgroundChanging />
        <Ui.LinearGradient />

        <Ui.ContentContainer>
          <Ui.MsgLogo />

          <Ui.FormWrapper>
            <Ui.EmailForm onSubmit={handleSubmit(onSubmit)}>
              <Ui.EmailLabel>{t.welcome_inputLabel}</Ui.EmailLabel>

              <Ui.Row $justifyCenter>
                <Col xs={12} md={8} xl={6} fullHD={4}>
                  <Ui.EmailInput
                    {...register(CheckEmailFormFields.Email, {
                      setValueAs: (value) => value.trim(),
                    })}
                    placeholder={t.welcome_inputPlaceholder}
                    id='email'
                  />

                  {fieldErrors && (
                    <AuthFormUi.ErrorContentWrapper>
                      <AuthFormUi.ErrorLine>{fieldErrors.message}</AuthFormUi.ErrorLine>
                    </AuthFormUi.ErrorContentWrapper>
                  )}
                </Col>
              </Ui.Row>

              <Ui.Row $justifyCenter>
                <Col xs={12} md={8} xl={4}>
                  <Ui.SubmitButton type='submit'>{t.welcome_submitButton}</Ui.SubmitButton>
                </Col>
              </Ui.Row>
            </Ui.EmailForm>
          </Ui.FormWrapper>

          <Ui.LegalCopyTextRow $justifyCenter>
            <Col xs={12} md={9} xl={6} fullHD={4}>
              <Ui.LegalCopyText data-cy='legal-text'>
                {t.welcome_legalText_1}
                <br />
                <br />
                {t.welcome_legalText_2}
                <Ui.LegalLink to={LEGAL_LINK.faqs}>{t.welcome_faqLinkText}</Ui.LegalLink>
              </Ui.LegalCopyText>
            </Col>
          </Ui.LegalCopyTextRow>
        </Ui.ContentContainer>
      </Ui.ContentWrapper>

      {promotion && showPromotionModal && (
        <PromotionModal promo={promotion} customSteps={customSteps} />
      )}
    </Layout>
  );
};

export default Welcome;
